import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { auth, clearToken, getToken } from '../api/client';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const authCheckInProgress = useRef(false);

  const checkAuth = useCallback(async () => {
    // Prevent multiple simultaneous auth checks
    if (authCheckInProgress.current) {
      // Return existing promise result
      return { skipped: true };
    }
    authCheckInProgress.current = true;
    
    // Skip auth check if no token exists and not processing a login
    const hasToken = !!getToken();
    if (!hasToken && !window.location.hash.includes('session_id')) {
      setLoading(false);
      authCheckInProgress.current = false;
      return { success: false, noToken: true };
    }
    
    try {
      const response = await auth.getMe();
      setUser(response.data);
      return { success: true, user: response.data };
    } catch (error) {
      console.log('[Auth] Check failed:', error?.response?.status);
      // Only clear user if we're not in the middle of processing a login
      if (!window.location.hash.includes('session_id')) {
        setUser(null);
        // Clear invalid token
        if (error?.response?.status === 401) {
          clearToken();
        }
      }
      return { success: false, status: error?.response?.status };
    } finally {
      setLoading(false);
      authCheckInProgress.current = false;
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const logout = useCallback(async () => {
    try {
      await auth.logout();
    } catch (e) {
      // ignore
    }
    clearToken(); // Clear localStorage token
    setUser(null);
  }, []);

  const setUserData = useCallback((userData) => {
    setUser(userData);
    setLoading(false);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth, setUserData }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
